import React from 'react';
import { Sparkles, Eye, Radio } from 'lucide-react';
import { Card } from './ui/card';

/**
 * FederationVideo Component — Federation Hub only (Tier 1 · Feb 2026).
 *
 * Every video surface across the app now routes to the Federation Video Hub
 * at federation-hub-7.preview.emergentagent.com/video. External video embeds
 * are prohibited per Architect mandate.
 *
 * Props are accepted for backward compatibility with old call sites
 * (title, description, videoUrl) but videoUrl is intentionally ignored — the
 * Hub is the single source of truth.
 */

const FEDERATION_VIDEO_HUB_URL = 'https://federation-hub-7.preview.emergentagent.com/video';

const FederationVideo = ({
  title = 'A Mirror for Your Mind',
  description = 'Explore the depths of consciousness — every video, every Federation node, one Hub.',
}) => {
  return (
    <div
      data-testid="federation-video-hub"
      style={{ margin: '3rem auto', maxWidth: '1200px', padding: '0 2rem' }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem',
          }}
        >
          <Eye size={22} color="#00ffff" />
          <span
            style={{
              fontSize: '0.8rem',
              color: '#00ffff',
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              fontWeight: 700,
            }}
          >
            Federation Video Hub
          </span>
          <Sparkles size={22} color="#00ffff" />
        </div>

        <h2
          style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            background: 'linear-gradient(135deg, #00ffff, #ff00ff, #00ff88)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold',
            marginBottom: '0.5rem',
          }}
        >
          {title}
        </h2>

        <p
          style={{
            fontSize: '1rem',
            color: '#aaa',
            maxWidth: '640px',
            margin: '0 auto',
          }}
        >
          {description}
        </p>
      </div>

      {/* Hub Embed */}
      <Card
        style={{
          background:
            'linear-gradient(135deg, rgba(0,255,255,0.05), rgba(255,0,255,0.05))',
          border: '2px solid rgba(0,255,255,0.3)',
          borderRadius: '1rem',
          overflow: 'hidden',
          position: 'relative',
          boxShadow: '0 0 40px rgba(0,255,255,0.2), 0 0 80px rgba(255,0,255,0.1)',
        }}
      >
        <iframe
          src={FEDERATION_VIDEO_HUB_URL}
          title="Federation Video Hub"
          data-testid="federation-video-hub-iframe"
          loading="lazy"
          allow="autoplay; encrypted-media; clipboard-write; picture-in-picture"
          style={{
            display: 'block',
            width: '100%',
            height: '520px',
            border: 'none',
            background: '#0a0a0f',
          }}
        />

        <div
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'rgba(0,0,0,0.8)',
            padding: '0.5rem 1rem',
            borderRadius: '2rem',
            border: '1px solid rgba(0,255,255,0.5)',
            fontSize: '0.72rem',
            color: '#00ffff',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Radio size={13} />
          <span>Federation Owned</span>
        </div>
      </Card>

      <div
        style={{
          textAlign: 'center',
          marginTop: '1.25rem',
          fontSize: '0.85rem',
          color: '#666',
          fontStyle: 'italic',
        }}
      >
        <span style={{ color: '#00ffff' }}>●</span> Federation-hosted · no external tracking
        <span style={{ margin: '0 1rem', color: '#444' }}>|</span>
        <a
          href={FEDERATION_VIDEO_HUB_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="federation-video-hub-open"
          style={{ color: '#ff00ff', textDecoration: 'none' }}
        >
          Open Hub in new tab →
        </a>
      </div>
    </div>
  );
};

export default FederationVideo;
